import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowUpRight, BookOpen, Check, ChevronDown, Info, LayoutDashboard, Heart, Loader2 } from 'lucide-react';
import { contentTagService, PublicContentTagDto } from '../services/contentTagService';
import { contentService, TaggedContentBlockDto, TaggedContentLessonDto, ContentBlockDto } from '../services/contentService';
import { useNotifications } from './feedback/NotificationSystem';
import { PersianOverlayLoader } from './shared/PersianLoader';
import { TagBadge } from './shared/TagBadge';
import { ResponsiveContainer } from './shared/MobileResponsive';
import { colorWithAlpha, darkenColor } from '../utils/colorUtils';
import { useTheme } from '../hooks/useTheme';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { errorLoggingService } from '../services/errorLoggingService';
import { useRefreshStickyAudioMap, useStickyAudio } from '../contexts/StickyAudioContext';
import { ContentBlockDisplay, assignLessonBlockTourIds } from './learning/LessonViewer';
import { TagSourceLessonChoice, type TagSourceLessonOption } from './learning/TagSourceLessonChoice';
import { WordHelpProvider } from '../contexts/WordHelpContext';
import { LessonPageHero } from './learning/LessonPageHero';
import { SimplePageHeader } from './shared/SimplePageHeader';
import { CommentModal } from './learning/CommentModal';
import { LearnerRatingCommentsTrigger } from './shared/LearnerRatingCommentsTrigger';
import { LearnerHeroViewFavoriteCluster } from './shared/LearnerHeroViewFavoriteCluster';
import { commentService } from '../services/commentService';
import { formatRatingOneDecimal, ratingAverageFromSummary } from '../utils/formatRating';
import { favoriteService } from '../services/favoriteService';
import { useLessonTypographyPreferences } from '../hooks/useLessonTypographyPreferences';
import { saveLastTagPosition } from '../utils/lastLearningPosition';
import { CollapsibleFadePanel } from './shared/CollapsibleFadePanel';
import { TagViewerPager } from './shared/TagViewerNavFilterBar';
import { TagLessonViewerPanel } from './TagLessonViewerPanel';
import { TagBlockSourceLessons, useOpenTagExercise } from './TagBlockSourceLessons';
import { PassedLessonBanner } from './learning/PassedLessonBanner';
import { getIconByName } from '../utils/iconUtils';
import { LessonMapPanel } from './learning/LessonMapPanel';
import { useLocalizedContent } from '../hooks/useLocalizedContent';
import { useAppLocale } from '../providers/LocaleProvider';
import { useLearnerHomeDesign } from '../utils/learnerHomeDesign';
import { lessonHeroEyebrow } from '../utils/lessonHeroLabel';
import { localeMirroredNavArrowClass } from '../utils/localeNavArrow';
import {
  buildTagBlockLessonMap,
  buildTagLessonsMap,
  type LessonMapItem
} from '../utils/lessonMapSections';
import {
  buildTagBlockLessons,
  findTagBlockLessonIndex,
  isTagBlockLessonComplete
} from '../utils/tagBlockLessons';

interface TagContentViewProps {
  tagId: number;
  tag?: PublicContentTagDto;
  initialBlockId?: number;
  initialLessonId?: number;
  refreshTrigger?: number;
  onBack: () => void;
  onNavigateToLesson?: (lessonId: number) => void;
}

/** Map tagged block to ContentBlockDto for full block display */
const TagTaggedBlockDisplay: React.FC<{
  block: TaggedContentBlockDto;
  tourIds: { stickyAudioTourId?: string; wordHelpTourId?: string; wordHelpScopeTourId?: string; tipTourId?: string };
  lessonTypography: ReturnType<typeof useLessonTypographyPreferences>;
}> = ({ block, tourIds, lessonTypography }) => {
  const openExercise = useOpenTagExercise();
  const blockDto = toContentBlockDto(block);
  return (
    <ContentBlockDisplay
      block={blockDto}
      lessonId={block.lessonId}
      countsTowardLessons={false}
      lessonTypography={lessonTypography}
      pinWordHelp={false}
      onStartInlineExercise={(exerciseId) => openExercise(exerciseId)}
      {...(tourIds.stickyAudioTourId ? { stickyAudioTourId: tourIds.stickyAudioTourId } : {})}
      {...(tourIds.wordHelpTourId ? { wordHelpTourId: tourIds.wordHelpTourId } : {})}
      {...(tourIds.wordHelpScopeTourId ? { wordHelpScopeTourId: tourIds.wordHelpScopeTourId } : {})}
      {...(tourIds.tipTourId ? { tipTourId: tourIds.tipTourId } : {})}
    />
  );
};

function toContentBlockDto(block: TaggedContentBlockDto): ContentBlockDto {
  return {
    id: block.id,
    lessonId: block.lessonId,
    type: block.type,
    displayOrder: block.displayOrder,
    title: block.title,
    titleFa: block.titleFa,
    textContent: block.textContent,
    audioUrl: block.audioUrl,
    audioFileName: block.audioFileName,
    imageUrl: block.imageUrl,
    videoUrl: block.videoUrl,
    transcript: block.transcript,
    durationSeconds: block.durationSeconds,
    tips: block.tips,
    stickyAudio: block.stickyAudio,
    textFormatting: block.textFormatting,
    isDownloadable: block.isDownloadable ?? false,
    audioSyncData: block.audioSyncData,
    ...(block.lessonExerciseId != null ? { lessonExerciseId: block.lessonExerciseId } : {}),
    vocabularyItems: block.vocabularyItems ?? [],
    createdAt: ''
  } as ContentBlockDto;
}

const TAG_BLOCK_SOURCE_NOTE_KEY = 'persianist.tagBlockSourceNoteCollapsed';

function readTagBlockSourceNoteCollapsed(): boolean {
  try {
    return localStorage.getItem(TAG_BLOCK_SOURCE_NOTE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeTagBlockSourceNoteCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(TAG_BLOCK_SOURCE_NOTE_KEY, collapsed ? '1' : '0');
  } catch {
    /* private mode */
  }
}

function uniqueSourceLessons(blocks: TaggedContentBlockDto[]): TagSourceLessonOption[] {
  const lessons: TagSourceLessonOption[] = [];
  for (const block of blocks) {
    if (!block.lessonId || lessons.some((lesson) => lesson.lessonId === block.lessonId)) continue;
    lessons.push({
      lessonId: block.lessonId,
      lessonTitle: block.lessonTitle,
      courseTitle: block.courseTitle,
      levelTitle: block.levelTitle
    });
  }
  return lessons;
}

/** Footnote under the tag-block pager. Collapsed for every tag once the learner dismisses it. */
const TagBlockSourceNote: React.FC<{
  simple: boolean;
  isRtl: boolean;
  blocks: TaggedContentBlockDto[];
  onNavigateToLesson: (lessonId: number) => void;
}> = ({ simple, isRtl, blocks, onNavigateToLesson }) => {
  const { t } = useTranslation('learning');
  const lessons = uniqueSourceLessons(blocks);
  const linkRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(() => !readTagBlockSourceNoteCollapsed());
  const [choiceOpen, setChoiceOpen] = useState(false);
  const lessonKey = lessons.map((lesson) => lesson.lessonId).join(',');
  useEffect(() => {
    setChoiceOpen(false);
  }, [lessonKey]);
  if (lessons.length === 0) return null;

  const copy = (key: string, en: string, fa: string) =>
    t(key, { defaultValue: isRtl ? fa : en });

  const openFullLesson = () => {
    const only = lessons.length === 1 ? lessons[0] : undefined;
    if (only) {
      onNavigateToLesson(only.lessonId);
      return;
    }
    setChoiceOpen(true);
  };

  const dismiss = () => {
    writeTagBlockSourceNoteCollapsed(true);
    setChoiceOpen(false);
    setOpen(false);
  };

  const expand = () => {
    writeTagBlockSourceNoteCollapsed(false);
    setOpen(true);
  };

  const cardClass = simple
    ? 'rounded-2xl border border-[#e4d5c3] bg-[#fffdf8] px-3.5 py-3 text-[#6d5644] shadow-[0_10px_28px_-20px_rgba(63,42,86,0.55)] dark:border-[#3d342c] dark:bg-[#1c1714] dark:text-[#d4b48a]'
    : 'rounded-2xl border border-violet-200/80 bg-violet-50/70 px-3.5 py-3 text-gray-600 shadow-sm dark:border-violet-800/45 dark:bg-violet-950/30 dark:text-gray-300';
  const iconClass = simple
    ? 'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#f3e6d4] text-[#3f2a56] dark:bg-[#2a241f] dark:text-[#d4b48a]'
    : 'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-200';
  const linkClass = simple
    ? 'inline-flex items-center gap-0.5 align-baseline font-semibold text-[#3f2a56] underline decoration-[#c4a574] decoration-1 underline-offset-2 transition-colors hover:text-[#8a6a45] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3f2a56] dark:text-[#f6efe6] dark:decoration-[#8a6a45] dark:hover:text-[#d4b48a] dark:focus-visible:outline-[#d4b48a]'
    : 'inline-flex items-center gap-0.5 align-baseline font-semibold text-violet-800 underline decoration-violet-300 decoration-1 underline-offset-2 transition-colors hover:text-violet-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-500 dark:text-violet-200 dark:decoration-violet-500 dark:hover:text-white';
  const gotItClass = simple
    ? 'inline-flex h-5 items-center gap-1 rounded-full border border-[#e4d5c3] bg-white/80 px-1.5 text-[10px] font-normal leading-none text-[#3f2a56] transition-colors hover:bg-[#f7f1e8] dark:border-[#3d342c] dark:bg-[#241e1a] dark:text-[#f6efe6] dark:hover:bg-[#2a241f]'
    : 'inline-flex h-5 items-center gap-1 rounded-full border border-violet-200 bg-white px-1.5 text-[10px] font-normal leading-none text-violet-900 transition-colors hover:bg-violet-100/80 dark:border-violet-700 dark:bg-violet-950/50 dark:text-violet-100 dark:hover:bg-violet-900/50';
  const chipClass = simple
    ? 'me-auto inline-flex h-5 items-center gap-1 rounded-full border border-[#e4d5c3] bg-[#fffdf8] px-1.5 text-[10px] font-normal leading-none text-[#8a6a45] transition-colors hover:border-[#c4a574] hover:text-[#3f2a56] dark:border-[#3d342c] dark:bg-[#1c1714] dark:text-[#d4b48a] dark:hover:text-[#f6efe6]'
    : 'me-auto inline-flex h-5 items-center gap-1 rounded-full border border-violet-200/80 bg-white px-1.5 text-[10px] font-normal leading-none text-violet-900 transition-colors hover:bg-violet-50 dark:border-violet-800/50 dark:bg-violet-950/40 dark:text-violet-100 dark:hover:bg-violet-900/40';

  return (
    <div className="w-full">
      {open ? null : (
        <div dir={isRtl ? 'rtl' : 'ltr'} className="flex w-full">
          <button type="button" onClick={expand} className={chipClass} aria-expanded={false}>
            <Info className="h-2.5 w-2.5 shrink-0" aria-hidden />
            {copy('tagViewer.blockSourceNoteShow', 'About this block', 'درباره این بخش')}
            <ChevronDown className="h-2.5 w-2.5 shrink-0 opacity-70" aria-hidden />
          </button>
        </div>
      )}
      <CollapsibleFadePanel expanded={open} className={open ? '' : 'pointer-events-none'}>
        <div className={cardClass}>
          <div className="flex items-start gap-2.5 text-start">
            <span className={iconClass} aria-hidden>
              <Info className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] leading-snug">
                {copy(
                  'tagViewer.blockSourceNote',
                  "This block comes from another lesson based on this course's requirements. If you want to open the full lesson (which might not be related to this course),",
                  'این بخش از درس دیگری و بر اساس نیاز این دوره آمده است. اگر می‌خواهید درس کامل را باز کنید (که ممکن است به این دوره مربوط نباشد)،'
                )}{' '}
                <button type="button" ref={linkRef} onClick={openFullLesson} className={linkClass}>
                  {copy('tagViewer.blockSourceNoteLink', 'click here', 'اینجا کلیک کنید')}
                  <ArrowUpRight className={`h-3 w-3 shrink-0 ${isRtl ? '-scale-x-100' : ''}`} aria-hidden />
                </button>
                .
              </p>
              <div className="mt-2.5 flex justify-end">
                <button type="button" onClick={dismiss} className={gotItClass}>
                  <Check className="h-2.5 w-2.5 shrink-0" aria-hidden />
                  {copy('tagViewer.blockSourceNoteGotIt', 'Got it', 'فهمیدم')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleFadePanel>
      <TagSourceLessonChoice
        open={choiceOpen && lessons.length > 1}
        isRtl={isRtl}
        anchorRef={linkRef}
        lessons={lessons}
        onDismiss={() => setChoiceOpen(false)}
        onChoose={(lessonId) => {
          setChoiceOpen(false);
          onNavigateToLesson(lessonId);
        }}
      />
    </div>
  );
};

export const TagContentView: React.FC<TagContentViewProps> = ({ tagId, tag: propTag, initialBlockId, initialLessonId, refreshTrigger, onBack, onNavigateToLesson }) => {
  useRefreshStickyAudioMap();
  const { map: stickyAudioMap } = useStickyAudio();
  const { t } = useTranslation(['learning', 'notifications']);
  const { direction, locale } = useAppLocale();
  const isRtl = direction === 'rtl';
  const navArrowClass = (size: string) => localeMirroredNavArrowClass(isRtl, size);
  const { showError, showSuccess } = useNotifications();
  const { handleApiError } = useErrorHandler();
  const [tag, setTag] = useState<PublicContentTagDto | null>(propTag || null);
  const [blocks, setBlocks] = useState<TaggedContentBlockDto[]>([]);
  const [tagLessons, setTagLessons] = useState<TaggedContentLessonDto[]>([]);
  const [browseMode, setBrowseMode] = useState<'blocks' | 'lessons'>(() =>
    propTag?.target === 1 ? 'lessons' : 'blocks'
  );
  const [loading, setLoading] = useState(true);
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
  const [favoriteBlockIds, setFavoriteBlockIds] = useState<number[]>([]);
  const [favoriteTagToggling, setFavoriteTagToggling] = useState(false);
  const [showTagCommentModal, setShowTagCommentModal] = useState(false);
  const [tagRatingAvg, setTagRatingAvg] = useState<number | null>(null);
  const [tagRatingCount, setTagRatingCount] = useState(0);
  const [tagRatingLoading, setTagRatingLoading] = useState(false);
  const viewedTagLessonRef = useRef<string | null>(null);
  const appliedOpenPositionRef = useRef<string | null>(null);
  const { isDark } = useTheme();
  const [homeDesign] = useLearnerHomeDesign();
  const simpleHome = homeDesign === 'mme';
  const { pickTagName, pickTagDescription, pickBlockTitle } = useLocalizedContent();
  const tagDisplayName = tag ? pickTagName(tag) : '';
  const tagDisplayDescription = tag ? pickTagDescription(tag) : '';
  const lessonTypography = useLessonTypographyPreferences();

  useEffect(() => {
    if (propTag?.id === tagId) {
      setTag(propTag);
      return;
    }
    setTag(null);
  }, [tagId, propTag]);

  useEffect(() => {
    if (!tag) return;
    if (tag.target === 1) setBrowseMode('lessons');
    else setBrowseMode('blocks');
  }, [tag?.id, tag?.target]);

  useEffect(() => {
    loadTagContent();
  }, [tagId, refreshTrigger]);

  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const blockIds = await favoriteService.getFavoriteTagBlockIds(tagId);
        setFavoriteBlockIds(blockIds);
      } catch {
        setFavoriteBlockIds([]);
      }
    };

    void loadFavorites();
  }, [tagId]);

  const loadTagContent = async () => {
    try {
      setLoading(true);

      // Always refresh from public tags so published description fields stay populated
      // (propTag / resume navigation can omit or stale-cache descriptionPublished).
      let resolvedTag = tag?.id === tagId ? tag : null;
      try {
        const publicTags = await contentTagService.getPublicTags();
        const foundTag = publicTags.find((t) => t.id === tagId);
        if (foundTag) {
          resolvedTag = foundTag;
          setTag(foundTag);
        } else if (!resolvedTag) {
          const errorMessage = 'Tag not found';
          showError(errorMessage);
          errorLoggingService.logError(
            new Error(errorMessage),
            'api',
            { source: 'TagContentView', component: 'TagContentView', category: 'tag_content_loading', severity: 'medium', additionalData: { tagId, action: 'loadTag', url: window.location.href } }
          );
          return;
        }
      } catch (tagError) {
        if (!resolvedTag) {
          const errorMessage = tagError instanceof Error ? tagError.message : 'Failed to load tag';
          errorLoggingService.logError(
            tagError instanceof Error ? tagError : new Error(errorMessage),
            'api',
            { source: 'TagContentView', component: 'TagContentView', category: 'tag_content_loading', severity: 'medium', endpoint: '/api/ContentTags/public', additionalData: { tagId, action: 'loadTag', url: window.location.href, errorDetails: tagError instanceof Error ? (tagError as Error).stack : String(tagError) } }
          );
          handleApiError(tagError, 'tag_loading', () => loadTagContent());
          return;
        }
      }

      let tagBlocks: TaggedContentBlockDto[] = [];
      try {
        tagBlocks = await contentService.getBlocksByTag(tagId);
        setBlocks(tagBlocks);
        if (initialLessonId == null && initialBlockId) {
          const targetIndex = findTagBlockLessonIndex(buildTagBlockLessons(tagBlocks), initialBlockId);
          appliedOpenPositionRef.current = `${tagId}::${initialBlockId}`;
          if (targetIndex >= 0) {
            setBrowseMode('blocks');
            setCurrentBlockIndex(targetIndex);
          } else {
            setCurrentBlockIndex(0);
          }
        } else if (initialLessonId == null) {
          appliedOpenPositionRef.current = `${tagId}::`;
          setCurrentBlockIndex(0);
        }
      } catch (blocksError) {
        setBlocks([]);
        setCurrentBlockIndex(0);
        const errorMessage = blocksError instanceof Error ? blocksError.message : 'Failed to load content';
        errorLoggingService.logError(
          blocksError instanceof Error ? blocksError : new Error(errorMessage),
          'api',
          { source: 'TagContentView', component: 'TagContentView', category: 'tag_content_loading', severity: 'medium', endpoint: `/api/Content/tags/${tagId}/blocks`, additionalData: { tagId, action: 'loadBlocks', url: window.location.href, errorDetails: blocksError instanceof Error ? (blocksError as Error).stack : String(blocksError) } }
        );
        handleApiError(blocksError, 'tag_content_loading', () => loadTagContent());
      }

      try {
        const lessons = await contentService.getLessonsByTag(tagId);
        setTagLessons(lessons);
      } catch {
        setTagLessons([]);
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load content';
      errorLoggingService.logError(
        error instanceof Error ? error : new Error(errorMessage),
        'api',
        { source: 'TagContentView', component: 'TagContentView', category: 'tag_content_loading', severity: 'medium', additionalData: { tagId, url: window.location.href, errorDetails: error instanceof Error ? (error as Error).stack : String(error) } }
      );
      handleApiError(error, 'tag_content_loading', () => loadTagContent());
    } finally {
      setLoading(false);
    }
  };

  const blockLessons = useMemo(() => buildTagBlockLessons(blocks), [blocks]);
  const filteredBlockLessons = blockLessons;
  const filteredLessons = tagLessons;

  const hasBlockBrowse = blockLessons.length > 0;
  const hasLessonBrowse = tagLessons.length > 0;
  const isBlocksMode =
    hasBlockBrowse && hasLessonBrowse
      ? browseMode === 'blocks'
      : hasBlockBrowse;

  useEffect(() => {
    if (hasBlockBrowse && hasLessonBrowse) return;
    if (!hasBlockBrowse && hasLessonBrowse) setBrowseMode('lessons');
    else if (hasBlockBrowse && !hasLessonBrowse) setBrowseMode('blocks');
  }, [hasBlockBrowse, hasLessonBrowse]);

  const displayableCount = isBlocksMode ? filteredBlockLessons.length : filteredLessons.length;

  useEffect(() => {
    const openKey = `${tagId}:${initialLessonId ?? ''}:${initialBlockId ?? ''}`;
    if (appliedOpenPositionRef.current === openKey) return;

    if (initialLessonId != null) {
      if (tagLessons.length === 0) return;
      const idx = tagLessons.findIndex((l) => l.id === initialLessonId);
      if (idx < 0) return;
      appliedOpenPositionRef.current = openKey;
      setBrowseMode('lessons');
      setCurrentBlockIndex(idx);
      return;
    }

    if (initialBlockId) {
      if (blocks.length === 0) return;
      const idx = findTagBlockLessonIndex(buildTagBlockLessons(blocks), initialBlockId);
      if (idx < 0) return;
      appliedOpenPositionRef.current = openKey;
      setBrowseMode('blocks');
      setCurrentBlockIndex(idx);
      return;
    }

    appliedOpenPositionRef.current = openKey;
  }, [tagId, initialLessonId, initialBlockId, blocks, tagLessons]);

  // Keep currentBlockIndex in valid range when filter or data changes (shared index for block vs lesson pager)
  const safeContentIndex = Math.min(Math.max(0, currentBlockIndex), Math.max(0, displayableCount - 1));
  const currentBlockLesson = isBlocksMode ? (filteredBlockLessons[safeContentIndex] ?? filteredBlockLessons[0]) : undefined;
  const currentBlock = currentBlockLesson?.blocks[0];
  const currentBlockLessonPassed = currentBlockLesson ? isTagBlockLessonComplete(currentBlockLesson) : false;

  useEffect(() => {
    const anchorId = currentBlock?.id;
    const lessonKey = currentBlockLesson?.id;
    if (!isBlocksMode || !anchorId || !lessonKey) return;
    const key = `${tagId}:${lessonKey}`;
    if (viewedTagLessonRef.current === key) return;
    viewedTagLessonRef.current = key;
    contentService.recordTagBlockLessonView(tagId, anchorId).then((result) => {
      const count = result.viewCount;
      if (typeof count !== 'number') return;
      setBlocks((prev) => prev.map((block) => (block.id === anchorId ? { ...block, viewCount: count } : block)));
    }).catch(() => {
      if (viewedTagLessonRef.current === key) viewedTagLessonRef.current = null;
    });
  }, [isBlocksMode, tagId, currentBlockLesson?.id, currentBlock?.id]);

  useEffect(() => {
    const anchorId = currentBlock?.id;
    if (!isBlocksMode || !anchorId) {
      setTagRatingAvg(null);
      setTagRatingCount(0);
      setTagRatingLoading(false);
      return;
    }
    let cancelled = false;
    setTagRatingLoading(true);
    commentService.getTagBlockRatingSummary(tagId, anchorId, true).then((summary) => {
      if (cancelled) return;
      const avg = ratingAverageFromSummary(summary);
      const count = summary.ratingCount ?? 0;
      if (count > 0 && avg != null) {
        setTagRatingAvg(avg);
        setTagRatingCount(count);
      } else {
        setTagRatingAvg(null);
        setTagRatingCount(0);
      }
    }).catch(() => {
      if (!cancelled) {
        setTagRatingAvg(null);
        setTagRatingCount(0);
      }
    }).finally(() => {
      if (!cancelled) setTagRatingLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isBlocksMode, tagId, currentBlock?.id, showTagCommentModal]);
  const currentLesson = !isBlocksMode ? (filteredLessons[safeContentIndex] ?? filteredLessons[0]) : undefined;
  const isCurrentBlockFavorite = currentBlock ? favoriteBlockIds.includes(currentBlock.id) : false;
  const setCurrentBlockIndexSafe = (updater: (prev: number) => number) => {
    setCurrentBlockIndex(prev => Math.max(0, Math.min(displayableCount - 1, updater(prev))));
  };

  useEffect(() => {
    if (!tag) return;
    if (isBlocksMode && currentBlock) {
      saveLastTagPosition(tagId, currentBlock.id, {
        tagTitle: tag.namePublished,
        tagTitleFa: tag.namePublishedFa,
        itemTitle: currentBlock.title || pickBlockTitle(currentBlock) || `Block ${safeContentIndex + 1}`,
        itemTitleFa: currentBlock.titleFa,
        tagIcon: tag.icon,
        tagColor: tag.color,
        tagBackgroundImageUrl: tag.backgroundImageUrl,
        browseMode: 'blocks'
      });
    } else if (!isBlocksMode && currentLesson) {
      saveLastTagPosition(tagId, undefined, {
        tagTitle: tag.namePublished,
        tagTitleFa: tag.namePublishedFa,
        itemTitle: currentLesson.title,
        itemTitleFa: currentLesson.titleFa,
        lessonId: currentLesson.id,
        lessonIsDesignedForNonFarsiUsers: currentLesson.isDesignedForNonFarsiUsers,
        tagIcon: tag.icon,
        tagColor: tag.color,
        tagBackgroundImageUrl: tag.backgroundImageUrl,
        browseMode: 'lessons'
      });
    }
  }, [
    tagId,
    tagDisplayName,
    tag?.icon,
    tag?.color,
    tag?.backgroundImageUrl,
    isBlocksMode,
    currentBlock?.id,
    currentBlock?.title,
    currentLesson?.id,
    currentLesson?.title,
    safeContentIndex
  ]);

  const refreshTagLessonProgress = async () => {
    try {
      const lessons = await contentService.getLessonsByTag(tagId);
      setTagLessons((prev) => {
        const prevPassed = new Map(prev.map((l) => [l.id, l.isPassedForTag]));
        return lessons.map((l) => {
          const wasPassed = prevPassed.get(l.id);
          if (wasPassed === true && !l.isPassedForTag) {
            return { ...l, isPassedForTag: true };
          }
          if (wasPassed === false && l.isPassedForTag) {
            return { ...l, isPassedForTag: false };
          }
          return l;
        });
      });
    } catch {
      /* keep previous */
    }
  };

  const handleBlocksPassed = useCallback((blockIds: number[], passed: boolean) => {
    setBlocks((prev) => prev.map((block) => (blockIds.includes(block.id) ? { ...block, isPassedForTag: passed } : block)));
  }, []);

  const tagLessonMapData = useMemo(() => {
    if (isBlocksMode) {
      return buildTagBlockLessonMap(blocks, currentBlock?.id, locale);
    }
    return buildTagLessonsMap(tagLessons, currentLesson?.id, locale);
  }, [isBlocksMode, blocks, currentBlock?.id, tagLessons, currentLesson?.id, locale]);

  const handleTagLessonMapNavigate = (item: LessonMapItem) => {
    if (item.blockIndex != null) {
      setBrowseMode('blocks');
      setCurrentBlockIndex(item.blockIndex);
      return;
    }
    if (item.lessonIndex != null) {
      setBrowseMode('lessons');
      setCurrentBlockIndex(item.lessonIndex);
    }
  };

  const browseTabClass = (active: boolean) =>
    simpleHome
      ? `flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm ${
          active
            ? 'bg-[#3f2a56] text-[#fff8ee] dark:bg-[#d4b48a] dark:text-[#2b221b]'
            : 'text-[#8a6a45] hover:bg-[#f7f1e8] dark:text-[#d4b48a] dark:hover:bg-[#2a241f]'
        }`
      : `flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm ${
          active
            ? 'bg-white text-violet-950 shadow-sm dark:bg-violet-900/80 dark:text-violet-50'
            : 'text-violet-800/80 hover:bg-white/70 dark:text-violet-200/80 dark:hover:bg-violet-900/40'
        }`;

  const browseTabs =
    hasBlockBrowse && hasLessonBrowse ? (
      <div
        className={
          simpleHome
            ? 'flex gap-1 rounded-full border border-[#e4d5c3] bg-[#fffdf8] p-1 dark:border-[#3d342c] dark:bg-[#1c1714]'
            : 'flex gap-1 rounded-full border border-violet-200/70 bg-white/50 p-1 dark:border-violet-800/40 dark:bg-violet-950/25'
        }
        role="tablist"
        aria-label="Browse by content type"
      >
        <button
          type="button"
          role="tab"
          aria-selected={browseMode === 'blocks' ? 'true' : 'false'}
          onClick={() => {
            setBrowseMode('blocks');
            setCurrentBlockIndex(0);
          }}
          className={browseTabClass(browseMode === 'blocks')}
        >
          {t('tagViewer.browseTab.blocks', { count: blockLessons.length })}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={browseMode === 'lessons' ? 'true' : 'false'}
          onClick={() => {
            setBrowseMode('lessons');
            setCurrentBlockIndex(0);
          }}
          className={browseTabClass(browseMode === 'lessons')}
        >
          {t('tagViewer.browseTab.lessons', { count: tagLessons.length })}
        </button>
      </div>
    ) : null;

  const lessonModeEmpty = displayableCount === 0 ? (
    <div className="rounded-xl border border-gray-200 bg-gray-100 p-6 text-center dark:border-gray-600 dark:bg-gray-700/50">
      <p className="text-gray-600 dark:text-gray-400">{t('tagViewer.empty.noContent')}</p>
    </div>
  ) : null;

  const tagAccentForMap = tag ? (isDark ? darkenColor(tag.color, 0.26) : tag.color) : undefined;

  const lessonMapPanel = (
    <LessonMapPanel
      mapData={{
        ...tagLessonMapData,
        ...(tagDisplayName ? { subtitle: tagDisplayName } : {})
      }}
      loading={loading}
      onNavigate={handleTagLessonMapNavigate}
      {...(tagAccentForMap ? { accentColor: tagAccentForMap } : {})}
      persistOpenKey={`lesson-map-open:tag:${tagId}`}
      {...(simpleHome ? {} : { animateFocusChange: true })}
    />
  );

  if (loading) {
    return (
      <>
        <ResponsiveContainer maxWidth="xl">
          <PersianOverlayLoader isVisible message={t('tag.loading', { ns: 'learning' })} />
        </ResponsiveContainer>
        {lessonMapPanel}
      </>
    );
  }

  if (!tag) {
    return (
      <>
        <ResponsiveContainer maxWidth="xl">
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">{t('tagViewer.notFound')}</p>
            <button onClick={onBack} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              {t('lesson.goBack')}
            </button>
          </div>
        </ResponsiveContainer>
        {lessonMapPanel}
      </>
    );
  }

  if (!hasBlockBrowse && !hasLessonBrowse) {
    return (
      <>
        <ResponsiveContainer maxWidth="xl">
          <div className="bg-white rounded-lg shadow-md p-12 text-center dark:bg-gray-900 dark:border dark:border-gray-800 dark:text-gray-100">
          <div className="mb-4">
            <div
              className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4"
              style={{
                backgroundColor: colorWithAlpha(
                  isDark ? darkenColor(tag.color, 0.22) : tag.color,
                  isDark ? 0.28 : 0.12
                )
              }}
            >
              <TagBadge name={tagDisplayName} color={tag.color} size="lg" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">{t('tagViewer.noContentTitle')}</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('tagViewer.noContentBody', { tag: tagDisplayName })}
          </p>
          <button onClick={onBack} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
            <LayoutDashboard className="w-4 h-4" />
            {t('tagViewer.goBackDashboard')}
          </button>
        </div>
      </ResponsiveContainer>
      {lessonMapPanel}
      </>
    );
  }

  const TagHeaderIcon = tag?.icon ? getIconByName(tag.icon) : BookOpen;
  const heroTagDescription =
    tagDisplayDescription.trim() || t('tagViewer.heroSubtitle', { ns: 'learning' });
  const currentTagLessonNumber = isBlocksMode
    ? currentBlockLesson?.blocks.find((block) => (block.lessonNumber ?? 0) > 0)?.lessonNumber
    : currentLesson?.lessonNumber;
  const tagHeroEyebrow = lessonHeroEyebrow(
    t,
    (currentTagLessonNumber ?? 0) > 0
      ? currentTagLessonNumber
      : displayableCount > 0
        ? safeContentIndex + 1
        : null,
    locale
  );

  const lessonBetweenHeroAndJourney = (
    <div className="space-y-4">
      {browseTabs}
      {lessonModeEmpty}
    </div>
  );

  if (!isBlocksMode) {
    return (
      <>
        <div className="mx-auto max-w-6xl space-y-4 px-3 py-4 sm:p-6">
          <div data-tour-id="tag-content-viewer">
            {currentLesson ? (
              <>
              <TagLessonViewerPanel
                tagId={tagId}
                lessonId={currentLesson.id}
                isPassedForTag={currentLesson.isPassedForTag === true}
                tagName={tagDisplayName}
                {...(typeof currentLesson.lessonNumber === 'number' ? { tagLessonNumber: currentLesson.lessonNumber } : {})}
                tagDescription={heroTagDescription}
                tagIcon={TagHeaderIcon}
                onBack={onBack}
                onBackToDashboard={onBack}
                betweenHeroAndJourney={lessonBetweenHeroAndJourney}
                onLessonProgressUpdated={() => void refreshTagLessonProgress()}
                onTagPassedChange={(passed) => {
                  setTagLessons((prev) =>
                    prev.map((l) => (l.id === currentLesson.id ? { ...l, isPassedForTag: passed } : l))
                  );
                }}
              />
              {displayableCount > 1 ? (
                <div className="mt-4">
                  <TagViewerPager
                    displayableCount={displayableCount}
                    safeContentIndex={safeContentIndex}
                    onIndexChange={setCurrentBlockIndexSafe}
                    simple={simpleHome}
                  />
                </div>
              ) : null}
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex items-center gap-2 rounded-xl border border-violet-200/90 bg-white/80 px-3 py-2 text-xs font-semibold text-violet-950 shadow-sm transition hover:bg-white dark:border-violet-700/50 dark:bg-violet-950/40 dark:text-violet-50"
                  >
                    <ArrowLeft className={navArrowClass('h-4 w-4')} aria-hidden />
                    {t('lesson.goBack')}
                  </button>
                </div>
                {lessonBetweenHeroAndJourney}
              </div>
            )}
          </div>
        </div>
        {lessonMapPanel}
      </>
    );
  }

  const tagBlockTopBar = currentBlock ? (
            <>
              <div data-tour-id="tag-content-rating" className="min-w-0">
                <LearnerRatingCommentsTrigger
                  onOpenComments={() => setShowTagCommentModal(true)}
                  loading={tagRatingLoading}
                  average={tagRatingAvg}
                  ratingLabel={formatRatingOneDecimal(tagRatingAvg)}
                  ratingCount={tagRatingCount}
                  accent="blue"
                />
              </div>
              <LearnerHeroViewFavoriteCluster viewCount={currentBlock.viewCount ?? 0} isRtl={isRtl} viewTourId="tag-content-view">
              <button
                type="button"
                onClick={async () => {
                  if (!currentBlock || favoriteTagToggling) return;
                  setFavoriteTagToggling(true);
                  try {
                    if (isCurrentBlockFavorite) {
                      await favoriteService.removeFavoriteTagBlock(tagId, currentBlock.id);
                      setFavoriteBlockIds((prev) => prev.filter((id) => id !== currentBlock.id));
                      showSuccess(t('notifications:favorites.removed'));
                    } else {
                      await favoriteService.addFavoriteTagBlock(tagId, currentBlock.id);
                      setFavoriteBlockIds((prev) => (prev.includes(currentBlock.id) ? prev : [...prev, currentBlock.id]));
                      showSuccess(t('notifications:favorites.added'));
                    }
                  } catch (e) {
                    showError(e instanceof Error ? e.message : t('notifications:favorites.updateFailed'));
                  } finally {
                    setFavoriteTagToggling(false);
                  }
                }}
                disabled={favoriteTagToggling}
                className="group inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-md border-0 bg-transparent p-0 text-inherit outline-none transition-transform hover:scale-105 active:scale-95 disabled:pointer-events-none disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-rose-400/80 focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                title={
                  favoriteTagToggling
                    ? 'Updating favorite'
                    : isCurrentBlockFavorite
                      ? t('lesson.removeFromFavorites')
                      : t('lesson.addToFavorites')
                }
                aria-label={
                  favoriteTagToggling
                    ? 'Updating favorite'
                    : isCurrentBlockFavorite
                      ? t('lesson.removeFromFavorites')
                      : t('lesson.addToFavorites')
                }
                aria-busy={favoriteTagToggling}
              >
                {favoriteTagToggling ? (
                  <Loader2 className="pointer-events-none h-4 w-4 animate-spin text-rose-500 dark:text-rose-400" aria-hidden />
                ) : (
                  <Heart
                    className={
                      isCurrentBlockFavorite
                        ? 'pointer-events-none h-4 w-4 fill-rose-500 text-rose-600 dark:fill-rose-500 dark:text-rose-400'
                        : 'pointer-events-none h-4 w-4 text-gray-400 transition-colors group-hover:text-rose-500 dark:text-gray-500 dark:group-hover:text-rose-400'
                    }
                    aria-hidden
                  />
                )}
              </button>
              </LearnerHeroViewFavoriteCluster>
            </>
  ) : undefined;

  return (
    <>
    <div className="mx-auto max-w-6xl space-y-4 px-3 py-4 pb-8 sm:p-6">
      {/* ─── TOP PANEL: tag header (blocks mode) ─── */}
      {simpleHome ? (
        <div className="space-y-3">
          <SimplePageHeader
            tourId="tag-content-header"
            eyebrow={tagHeroEyebrow}
            title={tagDisplayName}
            onBack={onBack}
            backLabel={t('lesson.goBack')}
            above={
              tagBlockTopBar ? (
                <div className="flex min-w-0 flex-nowrap items-center justify-between gap-2">
                  {tagBlockTopBar}
                </div>
              ) : undefined
            }
          />
          {browseTabs}
        </div>
      ) : (
      <LessonPageHero
        tourId="tag-content-header"
        compactChrome
        eyebrow={tagHeroEyebrow}
        title={tagDisplayName}
        description={heroTagDescription}
        icon={TagHeaderIcon}
        topBar={tagBlockTopBar}
        belowIcon={
          <button
            type="button"
            onClick={onBack}
            className="inline-flex h-5 w-8 items-center justify-center rounded-md border border-violet-200/90 bg-white/70 text-violet-950 shadow-sm backdrop-blur-[2px] transition-all hover:border-violet-300 hover:bg-white/90 active:scale-[0.98] dark:border-violet-700/50 dark:bg-violet-950/40 dark:text-violet-50 dark:hover:border-violet-600/60 dark:hover:bg-violet-950/55 md:w-9"
            aria-label={t('lesson.goBack')}
            title={t('lesson.goBack')}
          >
            <ArrowLeft className={`h-3.5 w-3.5 ${isRtl ? 'rotate-180' : ''}`} aria-hidden />
          </button>
        }
        actionPlacement="underBack"
        footer={browseTabs ?? undefined}
        action={
          <button
            type="button"
            onClick={onBack}
            className="inline-flex h-5 w-8 items-center justify-center rounded-md border border-violet-200/90 bg-white/70 text-violet-950 shadow-sm backdrop-blur-[2px] transition-all hover:border-violet-300 hover:bg-white/90 active:scale-[0.98] dark:border-violet-700/50 dark:bg-violet-950/40 dark:text-violet-50 dark:hover:border-violet-600/60 dark:hover:bg-violet-950/55 md:w-9"
            aria-label={t('catalog.backToDashboard')}
            title={t('catalog.backToDashboard')}
          >
            <LayoutDashboard className="h-3.5 w-3.5" aria-hidden />
          </button>
        }
      />
      )}

      {displayableCount === 0 && (
        <div className="rounded-xl border border-gray-200 bg-gray-100 p-6 text-center dark:border-gray-600 dark:bg-gray-700/50">
          <p className="text-gray-600 dark:text-gray-400">{t('tagViewer.empty.noContent')}</p>
        </div>
      )}

      {/* ─── CONTENT PANEL: journey, tagged blocks, practice and quizzes ─── */}
      <div data-tour-id="tag-content-viewer" className="space-y-4">
      {currentBlockLessonPassed ? (
        <PassedLessonBanner label={t('lesson.passedSuccessfullyBanner')} />
      ) : null}
      <TagBlockSourceLessons
        tagId={tagId}
        scopeId={currentBlockLesson?.id ?? 'none'}
        blocks={currentBlockLesson?.blocks ?? []}
        passed={currentBlockLessonPassed}
        onBlocksPassed={handleBlocksPassed}
      >
      {currentBlock ? (
        <div className="space-y-4">
          {currentBlockLesson?.blocks.map((block, blockIndex) => {
            const blockDto = toContentBlockDto(block);
            const tourIds = blockIndex === 0
              ? (assignLessonBlockTourIds([blockDto], locale, stickyAudioMap, 'tag-content-word-help')[0] ?? {})
              : {};
            return (
          <WordHelpProvider
            key={block.id}
            blocks={[blockDto]}
            scopeKind="lesson"
            lessonId={block.lessonId}
            loadRemote
          >
          <TagTaggedBlockDisplay
            block={block}
            tourIds={tourIds}
            lessonTypography={lessonTypography}
          />
          </WordHelpProvider>
            );
          })}
        </div>
      ) : null}
      </TagBlockSourceLessons>

      {displayableCount > 1 && currentBlock ? (
        <TagViewerPager
          displayableCount={displayableCount}
          safeContentIndex={safeContentIndex}
          onIndexChange={setCurrentBlockIndexSafe}
          simple={simpleHome}
        />
      ) : null}
      {isBlocksMode && currentBlock && onNavigateToLesson ? (
        <TagBlockSourceNote
          simple={simpleHome}
          isRtl={isRtl}
          blocks={currentBlockLesson?.blocks ?? []}
          onNavigateToLesson={onNavigateToLesson}
        />
      ) : null}
      </div>

    </div>
    {lessonMapPanel}
    {isBlocksMode && currentBlock ? (
      <CommentModal
        tagId={tagId}
        tagBlockId={currentBlock.id}
        tagTitle={tagDisplayName}
        isOpen={showTagCommentModal}
        onClose={() => setShowTagCommentModal(false)}
      />
    ) : null}
    </>
  );
};
